import { OnInit, Input, Output, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { AnonymousSubscription } from 'rxjs/Subscription';
import { forEach, keyBy } from 'lodash';

import { PropertyNameRendererComponent } from './';
import { PropertyDetail, PropertyDetailJSON, MARKER_TYPE } from '~core/model';

import {
  SelectableGridBaseComponent,
  COLUMN_TYPE,
  SelectableGrid,
  GRID_HEIGHT,
  RowDefinition,
  ColumnDefinition,
  SelectionEvent,
  RowSelection,
  RowCheckBoxParentComponent
} from '~shared/components/grids';

const sortByPosition = <T extends PropertyDetail>(l: T, r: T) => {
  return (!!l.position ? l.position : 'z').localeCompare(!!r.position ? r.position : 'z', undefined, { numeric: true, sensitivity: 'base' }); // Extra parameters required for alphanumeric sort
};

declare type Comparator<T, R> = (l: T, r: T, row1: RowDefinition<R>, row2: RowDefinition<R>, isInverted: boolean) => number;

const sortByType = <T extends PropertyDetail>(l: T, r: T, isInverted: boolean) => {
  const multiplier = isInverted ? -1 : 1;

  if (!!l.position && !r.position) return -1 * multiplier;
  if (!l.position && !!r.position) return 1 * multiplier;

  return 0;
};

const sortStrings = (a: string, b: string) => {
  if (!a || !a.length) return 1;
  if (!b || !b.length) return -1;

  return a.toString()
    .localeCompare(
    b.toString(),
    undefined,
    { sensitivity: 'base' });
};

const stringSort: Comparator<string, RowSelection<PropertyDetail>> = (l, r, row1, row2, isInverted) => {
  const byType = sortByType(row1.data.data, row2.data.data, isInverted);
  if (byType !== 0) return byType;
  return sortStrings(l, r);
};

const numericSort: Comparator<number, RowSelection<PropertyDetail>> = (l, r, row1, row2, isInverted) => {
  const byType = sortByType(row1.data.data, row2.data.data, isInverted);
  if (byType !== 0) return byType;
  return l - r;
};

@SelectableGrid({
  selector: 'property-grid',
  height: GRID_HEIGHT.FULL,
  width: 'overflow'
})
export class PropertyGridComponent extends SelectableGridBaseComponent<PropertyDetail> implements OnInit, OnDestroy {
  @Input() properties: Observable<PropertyDetailJSON[]>;
  @Output() onChangePropertySelect: Subject<SelectionEvent> = new Subject<SelectionEvent>();
  dataSubject: BehaviorSubject<RowSelection<PropertyDetail>[]> = new BehaviorSubject([]);
  private _subscriptions: AnonymousSubscription[] = [];

  ngOnInit() {

    this.subjects.push(
      this.onChangePropertySelect,
      this.dataSubject
    );

    const propertyMetrics: Observable<RowSelection<PropertyDetail>[]> = this.properties
      .map(metrics => metrics
        .map(metric => new PropertyDetail(metric))
        .map(metric => ({ data: metric, isSelected: !!metric.position })))
      .share()
      .do(data => this.selectionChanged.next(data));

    this._subscriptions.push(propertyMetrics.subscribe(m => this.dataSubject.next(m)));

    this._subscriptions.push(this.onChangePropertySelect
      .withLatestFrom(propertyMetrics)
      .subscribe(([selectedProperty, properties]: [SelectionEvent, RowSelection<PropertyDetail>[]]) => {
        const rowsToUpdate = [];
        let selectedIndex = 1;

        if (selectedProperty) {
          const propertyMap = keyBy(properties, 'data.id');
          propertyMap[selectedProperty.id].isSelected = selectedProperty.isSelected;
          selectedIndex = 1;
        }

        properties.map(m => {
          if (m.isSelected) {
            m.data.updatePosition(MARKER_TYPE.SUBJECT, selectedIndex.toString());
            selectedIndex++;
          } else if (m.data.position) {
            m.data.updatePosition(MARKER_TYPE.NONE, '');
          } else {
            return;
          }
          rowsToUpdate.push(m);
        });

        if (!rowsToUpdate.length) return;

        forEach(rowsToUpdate, data => {
          this.setRowData(data.data.id, 'data.position', data.data.position);
          this.setRowData(data.data.id, 'isSelected', data.isSelected);
        });

        this.refreshRows(rowsToUpdate.map(row => row.data.id));
        this.refreshInMemoryRowModel('sort');
        this.selectionChanged.next(properties);

        this.dataSubject.next(properties);
      }));

    this.columnDefinitions.next(this.createColumnDefinitions());

    this.gridOptions = Observable.of({
      context: { componentParent: this },
      getRowNodeId: (data) => data.data.id,
      getRowClass: params => {
        if (params.data && params.data.isSelected) return 'selected-property';
        return null;
      }
    });

    this.data = this.dataSubject.asObservable()
      .map(data => ({ data }));

    this._subscriptions.push(this.grid.gridReady.subscribe((ready) => this.setSortModel('data.position', 'asc')));
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  createStringColumn(label: string, property: string, type: string, width = 40, params?: ColumnDefinition) {
    return this.createColumn(label, property, type, width, params)
      .useSorting(stringSort);
  }

  createNumericColumn(label: string, property: string, type: string, width = 40, params?: ColumnDefinition) {
    return this.createColumn(label, property, type, width, params)
      .useSorting(numericSort);
  }

  createColumnDefinitions() {
    return [
      this.createColumn('', 'propertyId', COLUMN_TYPE.NUMBER, 0)
        .pin()
        .hide(),
      this.createStringColumn('Name', 'propertyName', COLUMN_TYPE.STRING, 200)
        .pin()
        .useCellClass('text-left text-blue')
        .useHeaderClass('text-left')
        .useSorting(true)
        .useComponent(PropertyNameRendererComponent),
      this.createNumericColumn('Asking Rent', 'metrics.askingRent', COLUMN_TYPE.CURRENCY, 100)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Effective Rent', 'metrics.effectiveRent', COLUMN_TYPE.CURRENCY, 100)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Effective RPSF', 'metrics.effectiveRpsf', COLUMN_TYPE.CURRENCY, 150, { decimals: 3 })
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Concessions', 'metrics.concessions', COLUMN_TYPE.PERCENTAGE, 100, { decimals: 1 })
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Occupancy', 'metrics.occupancy', COLUMN_TYPE.PERCENTAGE, 100, { decimals: 1 })
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Unit Count', 'unitCount', COLUMN_TYPE.DECIMAL, 80)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Average SQFT', 'averageSquareFootage', COLUMN_TYPE.DECIMAL, 100)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Year Built', 'yearBuilt', COLUMN_TYPE.STRING, 80)
        .useSorting(true),
      this.createNumericColumn('Stories', 'stories', COLUMN_TYPE.NUMBER, 80)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createNumericColumn('Class', 'class', COLUMN_TYPE.STRING, 80)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createStringColumn('Address', 'address', COLUMN_TYPE.STRING, 150)
        .useCellClass('text-left')
        .useHeaderClass('text-left')
        .useSorting(true),
      this.createStringColumn('City', 'city', COLUMN_TYPE.STRING, 100)
        .useCellClass('text-left')
        .useHeaderClass('text-left')
        .useSorting(true),
      this.createStringColumn('ST', 'state', COLUMN_TYPE.STRING, 80)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true),
      this.createStringColumn('ZIP Code', 'zipCode', COLUMN_TYPE.STRING, 80)
        .useCellClass('text-center')
        .useHeaderClass('text-center')
        .useSorting(true)
    ];
  }

  onParentComponentCall(params, isSelected) {
    this.onChangePropertySelect.next({ id: params.node.data.id, isSelected });
  }
}



// WEBPACK FOOTER //
// ./src/app/features/property/components/grids/property.grid.ts